import React from "react";
import { Link } from "react-router-dom";
import { ArrowUp } from "lucide-react";

import { person, navSections, summary } from "../data/profile";

export default function Footer() {
  const year = new Date().getFullYear();

  return (
    <footer className="relative border-t border-white/[0.06] pb-10 pt-16">
      <div className="shell">
        {/* Oversized wordmark — the visual full stop for the page */}
        <p
          aria-hidden="true"
          className="select-none text-fluid-5xl font-display font-bold leading-[0.82] tracking-tighter text-white/[0.045]"
        >
          SOHAM DANG
        </p>

        <div className="mt-12 grid gap-10 border-t border-white/[0.06] pt-10 sm:grid-cols-2 lg:grid-cols-5">
          <div className="sm:col-span-2 lg:col-span-1">
            <p className="font-display text-base font-semibold text-slate-100">
              {person.name}
            </p>
            <p className="mt-1 text-[0.78rem] leading-relaxed text-slate-500">
              {person.headline}
            </p>
            <p className="mt-4 text-[0.78rem] text-slate-500">
              {person.location.city}, {person.location.region}, India
            </p>
            <a
              href={`mailto:${person.email}`}
              className="mt-2 block break-all font-mono text-[0.76rem] text-brand-300/90 transition-colors hover:text-brand-200"
            >
              {person.email}
            </a>
          </div>

          <nav aria-label="Footer sections">
            <p className="font-mono text-[0.58rem] uppercase tracking-label text-slate-600">
              Sections
            </p>
            <ul className="mt-4 space-y-2">
              {navSections.slice(1).map((s) => (
                <li key={s.id}>
                  <Link
                    to={`/#${s.id}`}
                    className="text-[0.8rem] text-slate-400 transition-colors hover:text-brand-300"
                  >
                    {s.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Footer hire">
            <p className="font-mono text-[0.58rem] uppercase tracking-label text-slate-600">
              Hire
            </p>
            <ul className="mt-4 space-y-2">
              {[
                ["Full stack developer", "/hire-full-stack-developer"],
                ["MERN stack developer", "/hire-mern-stack-developer"],
                ["React developer", "/hire-react-developer"],
                ["Node.js developer", "/hire-nodejs-developer"],
                ["Technical lead", "/hire-technical-lead"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-[0.8rem] text-slate-400 transition-colors hover:text-brand-300"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Footer explore">
            <p className="font-mono text-[0.58rem] uppercase tracking-label text-slate-600">
              Explore
            </p>
            <ul className="mt-4 space-y-2">
              {[
                ["Services", "/services"],
                ["About Soham", "/about"],
                ["All projects", "/projects"],
                ["SaaS development", "/saas-development"],
                ["API development", "/api-development"],
                ["What a full stack developer does", "/full-stack-developer"],
                ["The MERN stack explained", "/mern-stack-developer"],
              ].map(([label, to]) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-[0.8rem] text-slate-400 transition-colors hover:text-brand-300"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <nav aria-label="Footer elsewhere">
            <p className="font-mono text-[0.58rem] uppercase tracking-label text-slate-600">
              Elsewhere
            </p>
            <ul className="mt-4 space-y-2">
              <li>
                <a
                  href={person.social.linkedin}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="text-[0.8rem] text-slate-400 transition-colors hover:text-brand-300"
                >
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  href={person.social.github}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="text-[0.8rem] text-slate-400 transition-colors hover:text-brand-300"
                >
                  GitHub
                </a>
              </li>
              <li>
                <a
                  href={person.social.instagram}
                  target="_blank"
                  rel="noopener noreferrer me"
                  className="text-[0.8rem] text-slate-400 transition-colors hover:text-brand-300"
                >
                  Instagram
                </a>
              </li>
              <li>
                <Link
                  to="/blogs"
                  className="text-[0.8rem] text-slate-400 transition-colors hover:text-brand-300"
                >
                  Blog
                </Link>
              </li>
            </ul>
          </nav>
        </div>

        {/* GEO/AEO: one plain-language entity paragraph, in the DOM on every
            page, describing who this site is about. Crawlers and LLM
            fetchers that only read the tail of a document still get it. */}
        <p className="sr-only-seo">{summary}</p>

        <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-white/[0.06] pt-6">
          <p className="text-[0.72rem] text-slate-600">
            © {year} {person.name}. Built with React, Three.js and too much
            coffee.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="group inline-flex items-center gap-2 text-[0.72rem] text-slate-500 transition-colors hover:text-brand-300"
          >
            Back to top
            <ArrowUp className="h-3 w-3 transition-transform group-hover:-translate-y-0.5" />
          </button>
        </div>
      </div>
    </footer>
  );
}
